import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
const LS_KEY = (id) => `ssv2:forms:${id}:lastTest`;
export default function FormNotificationsTab({ draft, setDraft, smtpReady, formId }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [showPayload, setShowPayload] = useState(false);
    // Persist the last test result so users can see it across reloads.
    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY(formId));
            if (raw)
                setResult(JSON.parse(raw));
        }
        catch { /* ignore */ }
    }, [formId]);
    function persistResult(r) {
        setResult(r);
        try {
            if (r)
                localStorage.setItem(LS_KEY(formId), JSON.stringify(r));
            else
                localStorage.removeItem(LS_KEY(formId));
        }
        catch { /* ignore */ }
    }
    async function runTestWebhook() {
        setBusy(true);
        try {
            const r = await api.testWebhook(formId);
            persistResult({ ok: r.result.ok, http: r.result.http, error: r.result.error, used_url: r.result.used_url, at: Date.now() });
        }
        catch (e) {
            persistResult({ ok: false, http: 0, error: e?.message ?? 'Test failed', used_url: '', at: Date.now() });
        }
        finally {
            setBusy(false);
        }
    }
    // Sample payload preview: synthesize values like the test endpoint does
    // so the user knows exactly what shape gets posted.
    const samplePayload = useMemo(() => buildSamplePayload(draft), [draft]);
    return (_jsxs("div", { className: "form-tab-pane two-pane", children: [_jsxs("div", { className: "pane-main", children: [_jsxs("section", { className: "panel-card", children: [_jsxs("header", { children: [_jsxs("h3", { children: [_jsx("span", { className: "panel-icon", children: "\uD83C\uDF10" }), " Webhook"] }), _jsxs("p", { className: "muted small", children: ["Every successful submit POSTs JSON to this URL. The payload includes submitted values, hidden fields, the recipient list (", _jsx("code", { children: "notify_emails" }), "), field labels & types, the submission ID, and a deep link to the admin so a downstream automation (n8n, Make, Zapier\u2026) can route notifications without us doing SMTP."] })] }), _jsxs("div", { className: "webhook-row", children: [_jsxs("label", { className: "field", style: { flex: 1, minWidth: 0 }, children: [_jsx("span", { children: "Webhook URL" }), _jsx("input", { type: "url", value: draft.webhook_url ?? '', onChange: (e) => setDraft({ ...draft, webhook_url: e.target.value }), placeholder: "https://hooks.example.com/forms/contact-us" }), _jsxs("span", { className: "muted small", children: ["Leave empty to use the site-wide ", _jsx("code", { children: "N8N_WEBHOOK_URL" }), " from the env, or to skip webhook delivery."] })] }), _jsx("div", { className: "webhook-actions", children: _jsx("button", { type: "button", className: "primary-btn", onClick: runTestWebhook, disabled: busy, title: "Send a synthetic payload to confirm the URL is reachable", children: busy ? 'Sending…' : '⚡ Test webhook' }) })] })] }), _jsxs("section", { className: "panel-card", children: [_jsxs("header", { children: [_jsxs("h3", { children: [_jsx("span", { className: "panel-icon", children: "\uD83D\uDCE7" }), " Email recipients"] }), _jsx("p", { className: "muted small", children: "When SMTP is configured, every successful submit also sends an HTML summary to each address." })] }), _jsx(ChipList, { value: draft.notify_emails ?? [], onChange: (list) => setDraft({ ...draft, notify_emails: list }), placeholder: "alice@example.com", validate: (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) }), !smtpReady && (draft.notify_emails ?? []).length > 0 && (_jsxs("div", { className: "callout warn-callout", role: "status", children: ["\u26A0 SMTP isn't configured \u2014 emails won't actually go out.", ' ', _jsx(Link, { to: "/settings?tab=email", children: "Configure SMTP \u2192" })] })), _jsxs("p", { className: "muted small", style: { margin: 0 }, children: ["The webhook payload also carries this list as ", _jsx("code", { children: "notify_emails" }), ", so an automation can do its own routing if you prefer."] })] })] }), _jsxs("aside", { className: "pane-side", children: [_jsxs("section", { className: "panel-card side-card", children: [_jsx("header", { children: _jsxs("h3", { children: [_jsx("span", { className: "panel-icon", children: "\uD83D\uDCE1" }), " Delivery status"] }) }), _jsxs("div", { className: "status-row", children: [_jsx("span", { className: "status-label", children: "SMTP:" }), smtpReady
                                        ? _jsx("span", { className: "tag ok-tag", children: "Configured" })
                                        : _jsxs(_Fragment, { children: [_jsx("span", { className: "tag warn-tag", children: "Not configured" }), _jsx(Link, { to: "/settings?tab=email", className: "link-look small", style: { marginLeft: 8 }, children: "Set up \u2192" })] })] }), _jsxs("div", { className: "status-row", children: [_jsx("span", { className: "status-label", children: "Recipients:" }), _jsx("span", { children: (draft.notify_emails ?? []).length })] }), _jsxs("div", { className: "status-row", children: [_jsx("span", { className: "status-label", children: "Webhook URL:" }), _jsx("span", { className: draft.webhook_url ? 'tag ok-tag' : 'tag muted-tag', children: draft.webhook_url ? 'Custom' : 'Site default' })] })] }), _jsxs("section", { className: "panel-card side-card", children: [_jsx("header", { children: _jsxs("h3", { children: [_jsx("span", { className: "panel-icon", children: "\uD83E\uDDEA" }), " Last test"] }) }), result ? (_jsxs("div", { className: `callout ${result.ok ? 'ok-callout' : 'warn-callout'}`, style: { marginBottom: 8 }, children: [_jsx("strong", { children: result.ok ? '✓ Delivered' : '✗ Failed' }), ' ', _jsxs("code", { children: ["HTTP ", result.http || '—'] }), _jsx("div", { className: "muted small", style: { marginTop: 4 }, children: relTime(result.at) }), result.used_url && _jsx("div", { className: "muted small break", children: _jsx("code", { children: result.used_url }) }), result.error && _jsx("div", { className: "muted small", children: result.error })] })) : (_jsxs("p", { className: "muted small", children: ["No tests run yet. Hit ", _jsx("strong", { children: "\u26A1 Test webhook" }), " to fire a synthetic payload."] })), result && (_jsx("button", { className: "ghost small", onClick: () => persistResult(null), children: "Clear" }))] }), _jsxs("section", { className: "panel-card side-card", children: [_jsxs("header", { className: "clickable-header", onClick: () => setShowPayload((v) => !v), children: [_jsxs("h3", { children: [_jsx("span", { className: "panel-icon", children: "\uD83D\uDCE6" }), " Sample payload ", showPayload ? '▾' : '▸'] }), _jsx("p", { className: "muted small", children: "Exact JSON shape posted to your webhook." })] }), showPayload && (_jsx("pre", { className: "codeblock payload-preview-block", children: JSON.stringify(samplePayload, null, 2) }))] })] })] }));
}
function ChipList({ value, onChange, placeholder, validate }) {
    const [input, setInput] = useState('');
    const [error, setError] = useState(null);
    function commit(text) {
        const trimmed = text.trim().replace(/[,;]+$/, '');
        if (!trimmed)
            return;
        if (validate && !validate(trimmed)) {
            setError(`"${trimmed}" doesn't look like a valid email`);
            return;
        }
        if (value.includes(trimmed)) {
            setError(`"${trimmed}" is already in the list`);
            return;
        }
        onChange([...value, trimmed]);
        setInput('');
        setError(null);
    }
    function remove(i) { onChange(value.filter((_, idx) => idx !== i)); }
    function onKeyDown(e) {
        if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
            if (input.trim()) {
                e.preventDefault();
                commit(input);
            }
        }
        if (e.key === 'Backspace' && !input && value.length) {
            remove(value.length - 1);
        }
    }
    function onBlur() {
        if (input.trim())
            commit(input);
    }
    function onPaste(e) {
        const pasted = e.clipboardData.getData('text');
        if (/[,\s]/.test(pasted)) {
            e.preventDefault();
            pasted.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean).forEach(commit);
        }
    }
    return (_jsxs("div", { className: "chip-list-wrap", children: [_jsxs("div", { className: "chip-list", children: [value.map((email, i) => (_jsxs("span", { className: "chip", children: [_jsx("span", { className: "chip-text", children: email }), _jsx("button", { type: "button", className: "chip-remove", onClick: () => remove(i), title: "Remove", children: "\u2715" })] }, i))), _jsx("input", { type: "text", className: "chip-input", value: input, onChange: (e) => { setInput(e.target.value); if (error)
                            setError(null); }, onKeyDown: onKeyDown, onBlur: onBlur, onPaste: onPaste, placeholder: value.length === 0 ? placeholder : '' })] }), _jsx("div", { className: "muted small", style: { marginTop: 4 }, children: error
                    ? _jsx("span", { className: "err", children: error })
                    : _jsxs(_Fragment, { children: ["Press ", _jsx("kbd", { children: "Enter" }), " or ", _jsx("kbd", { children: "," }), " to add. ", value.length, " recipient", value.length === 1 ? '' : 's', "."] }) })] }));
}
// ---------- helpers ----------
function buildSamplePayload(def) {
    const values = {};
    for (const f of def.fields) {
        if (f.type === 'gdpr' || f.type === 'hidden')
            continue;
        values[f.id] = sampleValue(f.type, f);
    }
    return {
        form_id: def.id,
        form_name: '[TEST] ' + (def.name || def.id),
        submission_id: 0,
        is_test: true,
        site: '<your site name>',
        site_url: '<your site URL>',
        admin_url: '',
        values,
        field_definitions: Object.fromEntries(def.fields.map((f) => [f.id, { type: f.type, label: f.label, required: !!f.required }])),
        notify_emails: def.notify_emails ?? [],
        success_action: def.success_action ?? 'message',
        success_message: def.success_message ?? '',
        ip: '0.0.0.0',
        ua: 'Form-test/1.0',
        referer: '',
        timestamp: new Date().toISOString(),
    };
}
function sampleValue(type, f) {
    switch (type) {
        case 'email': return 'test@example.com';
        case 'tel': return '+1 555 0100';
        case 'url': return 'https://example.com';
        case 'number': return 42;
        case 'date': return new Date().toISOString().slice(0, 10);
        case 'rating': return Number(f.scale_max ?? 5);
        case 'multiselect': return Array.isArray(f.options) ? f.options.slice(0, 1) : [];
        case 'select':
        case 'radio': return Array.isArray(f.options) ? (f.options[0] ?? '') : '';
        case 'checkbox': return '1';
        case 'textarea': return 'This is a test submission from the admin.';
        default: return 'test value';
    }
}
function relTime(ms) {
    const diff = Math.round((Date.now() - ms) / 1000);
    if (diff < 60)
        return diff + 's ago';
    if (diff < 3600)
        return Math.round(diff / 60) + 'm ago';
    if (diff < 86400)
        return Math.round(diff / 3600) + 'h ago';
    return Math.round(diff / 86400) + 'd ago';
}
